import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook to handle window resize events efficiently
 * Consolidates multiple resize listeners into a single event listener
 * @param {Function} callback - Function to call on resize
 * @param {Object} options - Configuration options
 * @param {number} options.debounceMs - Debounce delay in milliseconds (default: 100)
 * @param {boolean} options.enabled - Whether the listener is enabled (default: true)
 * @returns {Object} - { windowWidth, windowHeight, isMobile, isTablet, isDesktop }
 */
export const useWindowResize = (callback = null, { debounceMs = 100, enabled = true } = {}) => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  // Derived responsive breakpoints
  const isMobile = windowSize.width < 768;
  const isTablet = windowSize.width >= 768 && windowSize.width < 1024;
  const isDesktop = windowSize.width >= 1024;

  const debouncedCallback = useCallback(
    debounce(() => {
      const newSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      setWindowSize(newSize);

      if (callback) {
        callback(newSize);
      }
    }, debounceMs),
    [callback, debounceMs]
  );

  useEffect(() => {
    if (!enabled) return;

    // Set initial size
    const initialSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    setWindowSize(initialSize);

    // Add resize listener
    window.addEventListener('resize', debouncedCallback);

    return () => {
      window.removeEventListener('resize', debouncedCallback);
    };
  }, [debouncedCallback, enabled]);

  return {
    windowWidth: windowSize.width,
    windowHeight: windowSize.height,
    isMobile,
    isTablet,
    isDesktop,
  };
};

/**
 * Simple debounce utility function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Hook specifically for mobile detection
 * @param {number} breakpoint - Mobile breakpoint (default: 768)
 * @returns {boolean} - Whether current screen is mobile
 */
export const useIsMobile = (breakpoint = 768) => {
  const { windowWidth } = useWindowResize();
  return windowWidth < breakpoint;
};

/**
 * Hook for responsive breakpoints
 * @returns {Object} - { isMobile, isTablet, isDesktop, windowWidth }
 */
export const useResponsive = () => {
  const { windowWidth, isMobile, isTablet, isDesktop } = useWindowResize();
  return { isMobile, isTablet, isDesktop, windowWidth };
};
